import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Upload, X, Image as ImageIcon,
  Save, ArrowLeft, DollarSign, Layers, FileText,
  CheckCircle, AlertCircle, Star, TrendingUp, Zap, Check,
  ShoppingBag, Video,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../../api/services';
import { CURRENCIES, DEFAULT_CURRENCY, formatPrice } from '../../utils/currency';
import DigitalCouponsSection from '../../components/products/DigitalCouponsSection';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1').replace('/api/v1', '');

const inputStyle = {
  background: 'var(--c-primary-dark)',
  color: 'var(--c-ink)',
  border: '0.5px solid var(--c-border)',
  padding: '9px 12px',
  borderRadius: 8,
  fontSize: 13,
  width: '100%',
  outline: 'none',
  transition: 'border-color 0.15s',
};
const onFocus = e => (e.target.style.borderColor = 'rgb(var(--c-accent-green-rgb))');
const onBlur  = e => (e.target.style.borderColor = 'var(--c-border)');

function SectionHead({ icon: Icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: '0.5px solid var(--c-border)' }}>
      <div style={{ width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(var(--c-accent-green-rgb), 0.1)', flexShrink: 0 }}>
        <Icon size={13} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
      </div>
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.09em', margin: 0 }}>{title}</p>
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
      {children}{required && <span style={{ color: 'rgb(var(--c-accent-green-rgb))', marginLeft: 2 }}>*</span>}
    </p>
  );
}

const CATEGORIES = [
  { id: 'service',      label: 'Service',   icon: Zap },
  { id: 'formation',    label: 'Formation', icon: Star },
  { id: 'pack',         label: 'Pack',      icon: Package },
  { id: 'consultation', label: 'Conseil',   icon: TrendingUp },
];

const AVAIL_OPTS = [
  { id: 'available',  label: 'Disponible',   icon: CheckCircle },
  { id: 'preorder',   label: 'Pré-commande', icon: AlertCircle },
  { id: 'outofstock', label: 'Rupture',      icon: ShoppingBag },
];

export default function EditProduct() {
  const navigate   = useNavigate();
  const { id }     = useParams();

  const [loading, setLoading]               = useState(true);
  const [loadError, setLoadError]           = useState('');
  const [productName, setProductName]       = useState('');
  const [category, setCategory]             = useState('');
  const [price, setPrice]                   = useState('');
  const [currency, setCurrency]             = useState(DEFAULT_CURRENCY);
  const [stock, setStock]                   = useState('');
  const [description, setDescription]       = useState('');
  const [shortDesc, setShortDesc]           = useState('');
  const [availability, setAvailability]     = useState('available');
  const [productType, setProductType]       = useState('physical');
  const [existingCoupons, setExistingCoupons]   = useState([]);
  const [newCouponRows, setNewCouponRows]       = useState([]);
  const [deletingCoupons, setDeletingCoupons]   = useState(new Set());
  const [images, setImages]                 = useState([]);
  const [videos, setVideos]                 = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [dragActive, setDragActive]         = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [saveError, setSaveError]           = useState('');
  const [saveSuccess, setSaveSuccess]       = useState('');

  // ── Coupon helpers ──────────────────────────────────────────────────────
  const addCouponRow    = () => setNewCouponRows(p => [...p, { id: Date.now() + Math.random(), code: '' }]);
  const updateCouponRow = (rowId, val) => setNewCouponRows(p => p.map(r => r.id === rowId ? { ...r, code: val } : r));
  const removeCouponRow = (rowId) => setNewCouponRows(p => p.filter(r => r.id !== rowId));
  const handleDeleteExistingCoupon = async (couponId) => {
    setDeletingCoupons(prev => new Set([...prev, couponId]));
    try {
      await productService.deleteProductCoupon(id, couponId);
      setExistingCoupons(prev => prev.filter(c => c._id !== couponId));
    } catch (err) {
      console.error('Erreur suppression coupon:', err);
    } finally {
      setDeletingCoupons(prev => { const s = new Set(prev); s.delete(couponId); return s; });
    }
  };

  // ── Chargement du produit ───────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const response = await productService.getProductById(id);
        const p = response?.data || response;

        setProductName(p.name || '');
        setCategory(p.category || '');
        setPrice(p.price != null ? p.price.toString() : '');
        setDescription(p.description || '');
        setShortDesc(p.shortDescription || '');
        setStock(p.stock != null ? p.stock.toString() : '');
        setAvailability(p.availability || (p.isActive === false ? 'outofstock' : 'available'));
        setCurrency(p.currency || DEFAULT_CURRENCY);
        setProductType(p.type || 'physical');

        if (p.type === 'digital') {
          try {
            const res = await productService.getProductCoupons(p._id || id);
            setExistingCoupons(res?.data || []);
          } catch (_) {}
        }

        if (Array.isArray(p.images) && p.images.length > 0) {
          setImages(p.images.map((img, idx) => {
            const raw = typeof img === 'string' ? img : (img.url || '');
            const preview = raw.startsWith('http') ? raw : `${API_BASE}/uploads/products/${raw}`;
            return { id: Date.now() + idx, preview, name: raw, stored: true };
          }));
        }
        if (Array.isArray(p.videos) && p.videos.length > 0) {
          setVideos(p.videos.map((vid, idx) => ({ id: Date.now() + idx, name: typeof vid === 'string' ? vid : `Vidéo ${idx + 1}` })));
        }
      } catch (err) {
        setLoadError('Impossible de charger le produit.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // ── Image/vidéo handlers ────────────────────────────────────────────────
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files.map(f => ({ id: Date.now() + Math.random(), file: f, preview: URL.createObjectURL(f), name: f.name }))]);
  };
  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    setImages(prev => [...prev, ...files.map(f => ({ id: Date.now() + Math.random(), file: f, preview: URL.createObjectURL(f), name: f.name }))]);
  };
  const removeImage = (imgId) => {
    setImages(prev => { const next = prev.filter(i => i.id !== imgId); if (mainImageIndex >= next.length) setMainImageIndex(Math.max(0, next.length - 1)); return next; });
  };
  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    setVideos(prev => [...prev, ...files.map(f => ({ id: Date.now() + Math.random(), file: f, name: f.name }))]);
  };

  // ── Soumission ──────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSaveError(''); setSaveSuccess('');
    const missing = [];
    if (!productName.trim())                       missing.push('le nom');
    if (!category)                                 missing.push('la catégorie');
    if (!price.trim() || isNaN(parseFloat(price))) missing.push('le prix');
    if (!description.trim())                       missing.push('la description');
    if (missing.length) { setSaveError(`Veuillez renseigner ${missing.join(', ')}.`); return; }

    setSaving(true);
    try {
      const newFiles     = images.filter(img => img.file).map(img => img.file);
      const newFilenames = newFiles.length > 0 ? ((await productService.uploadImages(newFiles))?.data?.filenames || []) : [];
      const existingFilenames = images.filter(img => img.stored).map(img => img.name);

      await productService.updateProduct(id, {
        name: productName, category, price: parseFloat(price), currency,
        description, shortDescription: shortDesc,
        stock: stock ? parseInt(stock) : null,
        availability, type: productType,
        images: [...existingFilenames, ...newFilenames],
        videos: videos.map(v => v.name),
      });

      if (productType === 'digital') {
        const codes = newCouponRows.map(r => r.code).filter(c => c.trim());
        if (codes.length > 0) await productService.addProductCoupons(id, codes);
      }

      setSaveSuccess('Produit mis à jour avec succès !');
      setTimeout(() => navigate('/produits'), 1800);
    } catch (err) {
      setSaveError(
        err.response?.status === 401 ? 'Non autorisé. Reconnectez-vous.'
        : err.response?.data?.message || (err.request ? 'Impossible de contacter le serveur.' : 'Erreur inconnue.')
      );
    } finally {
      setSaving(false);
    }
  };

  const completionFields = [
    { label: 'Nom',         done: !!productName.trim() },
    { label: 'Catégorie',   done: !!category },
    { label: 'Prix',        done: !!price.trim() && !isNaN(parseFloat(price)) },
    { label: 'Description', done: !!description.trim() },
  ];
  const progress = Math.round((completionFields.filter(f => f.done).length / completionFields.length) * 100);
  const previewPrice = price && !isNaN(parseFloat(price)) ? formatPrice(parseFloat(price), currency) : null;

  // ── Écrans de chargement / erreur ───────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 mx-auto mb-4"
            style={{ borderWidth: 2, borderStyle: 'solid', borderColor: 'var(--c-border)', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }} />
          <p className="text-label">Chargement du produit…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="card p-6 text-center" style={{ maxWidth: 400, margin: '0 auto' }}>
        <AlertCircle size={32} className="mx-auto mb-3" style={{ color: 'var(--c-error)' }} />
        <p className="text-ink font-medium mb-2">{loadError}</p>
        <button onClick={() => navigate('/produits')} className="btn-primary mt-2">
          Retour aux produits
        </button>
      </div>
    );
  }

  return (
    <div>

      {/* ── Bande carton full-bleed ──────────────────────────────────────── */}
      <div
        className="-mx-8 -mt-8 px-8 pt-8 pb-6 mb-6"
        style={{
          background: 'var(--c-primary-card)',
          borderBottom: '0.5px solid var(--c-border)',
          backgroundImage: [
            'repeating-linear-gradient(-45deg, rgba(128,128,128,0.045) 0px, rgba(128,128,128,0.045) 1px, transparent 1px, transparent 7px)',
            'repeating-linear-gradient(45deg,  rgba(128,128,128,0.025) 0px, rgba(128,128,128,0.025) 1px, transparent 1px, transparent 7px)',
          ].join(', '),
        }}
      >
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <motion.button whileHover={{ scale: 1.05, x: -2 }} whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/produits')} className="btn-ghost p-2 flex-shrink-0">
                <ArrowLeft size={16} />
              </motion.button>
              <div>
                <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--c-text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>Catalogue</p>
                <h1 className="text-ink font-semibold" style={{ fontSize: 18 }}>
                  Modifier le produit
                  {productName && <span style={{ color: 'var(--c-text-muted)', fontWeight: 400, fontSize: 14 }}> — {productName}</span>}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => navigate('/produits')} className="btn-ghost" style={{ height: 36, fontSize: 13 }}>Annuler</button>
              <motion.button whileHover={{ scale: saving ? 1 : 1.03 }} whileTap={{ scale: saving ? 1 : 0.97 }}
                onClick={handleSubmit} disabled={saving} className="btn-primary disabled:opacity-50" style={{ height: 36, fontSize: 13 }}>
                {saving
                  ? <><div className="animate-spin rounded-full h-3.5 w-3.5 flex-shrink-0" style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'currentColor' }} />Mise à jour…</>
                  : <><Save size={14} />Mettre à jour</>}
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Contenu ──────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1060, margin: '0 auto' }}>

      {/* ── Alertes ──────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex items-center gap-3 rounded-xl p-4 mb-5"
            style={{ background: 'rgba(79,179,103,0.1)', border: '0.5px solid rgba(79,179,103,0.3)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(79,179,103,0.2)' }}>
              <Check size={15} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
            </div>
            <div>
              <p className="text-ink font-semibold text-sm">{saveSuccess}</p>
              <p className="text-label text-xs">Redirection en cours…</p>
            </div>
          </motion.div>
        )}
        {saveError && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex items-center gap-3 rounded-xl p-4 mb-5"
            style={{ background: 'rgba(224,84,84,0.08)', border: '0.5px solid rgba(224,84,84,0.3)' }}>
            <AlertCircle size={18} style={{ color: 'var(--c-error)', flexShrink: 0 }} />
            <p className="text-sm" style={{ color: 'var(--c-error)' }}>{saveError}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Layout two-column ─────────────────────────────────────────────── */}
      <div className="flex flex-col xl:flex-row gap-5">

        {/* ══ COLONNE GAUCHE ══════════════════════════════════════════════ */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* ── Type de produit ───────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }} className="card p-5">
            <SectionHead icon={Layers} title="Type de produit" />
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'physical', icon: Package, label: 'Physique', desc: 'Article livré en main propre' },
                { id: 'digital',  icon: Zap,     label: 'Digital',  desc: 'Coupon, code, licence…' },
              ].map(opt => (
                <motion.button key={opt.id} type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => { setProductType(opt.id); if (opt.id !== productType) { setExistingCoupons([]); setNewCouponRows([]); } }}
                  className="p-4 rounded-xl text-left transition-all"
                  style={productType === opt.id
                    ? { border: '1.5px solid rgb(var(--c-accent-green-rgb))', background: 'rgba(var(--c-accent-green-rgb), 0.07)' }
                    : { border: '1px solid var(--c-border)', background: 'var(--c-primary-dark)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <opt.icon size={16} style={{ color: productType === opt.id ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-muted)' }} />
                    {productType === opt.id && (
                      <div className="ml-auto w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'rgb(var(--c-accent-green-rgb))' }}>
                        <Check size={9} style={{ color: '#000' }} />
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-ink)', marginBottom: 2 }}>{opt.label}</p>
                  <p style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{opt.desc}</p>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* ── Gestion des coupons (digital uniquement) ──────────────── */}
          <AnimatePresence>
            {productType === 'digital' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}
                style={{ overflow: 'hidden' }}
              >
                <DigitalCouponsSection
                  existingCoupons={existingCoupons}
                  newCouponRows={newCouponRows}
                  onNewCouponChange={updateCouponRow}
                  onAddRow={addCouponRow}
                  onRemoveRow={removeCouponRow}
                  onDeleteExisting={handleDeleteExistingCoupon}
                  isDeleting={deletingCoupons}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Identité ──────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="card p-5 space-y-4">
            <SectionHead icon={Package} title="Identité" />
            <div>
              <FieldLabel required>Nom du produit</FieldLabel>
              <input type="text" value={productName} onChange={e => setProductName(e.target.value)}
                placeholder="Ex : Chatbot Premium IA, WiFi Zone 30 min…"
                style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>
            <div>
              <FieldLabel required>Catégorie</FieldLabel>
              <div className="grid grid-cols-4 gap-2">
                {CATEGORIES.map(cat => (
                  <button key={cat.id} type="button" onClick={() => setCategory(cat.id)}
                    className="py-3 px-2 rounded-lg text-center transition-all"
                    style={category === cat.id
                      ? { background: 'rgba(var(--c-accent-green-rgb), 0.1)', border: '1px solid rgb(var(--c-accent-green-rgb))' }
                      : { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                    <cat.icon size={15} style={{ margin: '0 auto 5px', display: 'block', color: category === cat.id ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-muted)' }} />
                    <p style={{ fontSize: 10, fontWeight: 600, color: category === cat.id ? 'var(--c-ink)' : 'var(--c-text-muted)' }}>{cat.label}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel>Accroche</FieldLabel>
              <input type="text" value={shortDesc} onChange={e => setShortDesc(e.target.value)}
                placeholder="Une ligne vendeuse (affichée dans la liste)"
                style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>
          </motion.div>

          {/* ── Tarification & Inventaire ─────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="card p-5 space-y-4">
            <SectionHead icon={DollarSign} title="Tarification & Inventaire" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FieldLabel required>Prix</FieldLabel>
                <div className="flex gap-2">
                  <input type="text" value={price} onChange={e => setPrice(e.target.value)}
                    placeholder="0.00" style={{ ...inputStyle, flex: 1, width: 'auto' }} onFocus={onFocus} onBlur={onBlur} />
                  <select value={currency} onChange={e => setCurrency(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', paddingLeft: 10, paddingRight: 10, cursor: 'pointer', flexShrink: 0 }} onFocus={onFocus} onBlur={onBlur}>
                    {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <FieldLabel>Stock</FieldLabel>
                <input type="text" value={stock} onChange={e => setStock(e.target.value)}
                  placeholder="Quantité ou vide = illimité"
                  style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
              </div>
            </div>
            <div>
              <FieldLabel>Disponibilité</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {AVAIL_OPTS.map(opt => (
                  <button key={opt.id} type="button" onClick={() => setAvailability(opt.id)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={availability === opt.id
                      ? { background: 'rgba(var(--c-accent-green-rgb), 0.1)', border: '1px solid rgb(var(--c-accent-green-rgb))', color: 'rgb(var(--c-accent-green-rgb))' }
                      : { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)', color: 'var(--c-text-muted)' }}>
                    <opt.icon size={12} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── Description ───────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="card p-5 space-y-4">
            <SectionHead icon={FileText} title="Description" />
            <div>
              <FieldLabel required>Description complète</FieldLabel>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Décrivez votre produit en détail…"
                rows={5} style={{ ...inputStyle, resize: 'vertical' }} onFocus={onFocus} onBlur={onBlur} />
              <p style={{ fontSize: 10, color: 'var(--c-text-muted)', marginTop: 4, textAlign: 'right' }}>{description.length} caractères</p>
            </div>
            <div>
              <FieldLabel>Accroche courte</FieldLabel>
              <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)}
                placeholder="Une brève description (1–2 lignes)"
                rows={2} style={{ ...inputStyle, resize: 'none' }} onFocus={onFocus} onBlur={onBlur} />
            </div>
          </motion.div>

          {/* ── Médias ────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card p-5">
            <SectionHead icon={ImageIcon} title="Médias" />
            <div
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
              className="rounded-xl text-center transition-all cursor-pointer"
              style={{ border: `2px dashed ${dragActive ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-border)'}`, background: dragActive ? 'rgba(var(--c-accent-green-rgb), 0.05)' : 'transparent', padding: '28px 16px' }}
              onClick={() => document.getElementById('edit-img-input').click()}
              onMouseEnter={e => { if (!dragActive) e.currentTarget.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
              onMouseLeave={e => { if (!dragActive) e.currentTarget.style.borderColor = 'var(--c-border)'; }}
            >
              <input type="file" id="edit-img-input" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
              <motion.div animate={{ y: dragActive ? -4 : 0 }} transition={{ duration: 0.2 }}
                className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)' }}>
                <Upload size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
              </motion.div>
              <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-ink)', marginBottom: 4 }}>{dragActive ? 'Déposez ici' : images.length > 0 ? 'Ajouter d\'autres images' : 'Glissez vos images ou cliquez'}</p>
              <p style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>PNG, JPG, WEBP · max 10 MB</p>
            </div>

            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
                <AnimatePresence>
                  {images.map((img, index) => (
                    <motion.div key={img.id} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }} className="relative group aspect-square">
                      <img src={img.preview} alt={img.name} className="w-full h-full object-cover rounded-lg"
                        style={{ border: index === mainImageIndex ? '2px solid rgb(var(--c-accent-green-rgb))' : '0.5px solid var(--c-border)' }}
                        onError={e => { e.target.style.display = 'none'; }} />
                      {index === mainImageIndex && (
                        <div className="absolute top-1 left-1 px-1 rounded" style={{ background: 'rgb(var(--c-accent-green-rgb))', color: '#000', fontSize: 9, lineHeight: '16px' }}>✓</div>
                      )}
                      {img.stored && <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.8)' }} title="Déjà enregistrée" />}
                      <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1" style={{ background: 'rgba(0,0,0,0.55)' }}>
                        {index !== mainImageIndex && (
                          <button type="button" onClick={() => setMainImageIndex(index)} className="p-1 rounded-md" style={{ background: 'rgba(var(--c-accent-green-rgb),0.9)' }}>
                            <Star size={11} style={{ color: '#000' }} />
                          </button>
                        )}
                        <button type="button" onClick={() => removeImage(img.id)} className="p-1 rounded-md" style={{ background: 'rgba(224,84,84,0.9)' }}>
                          <X size={11} style={{ color: '#fff' }} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            <div className="mt-4">
              <FieldLabel>Vidéos (optionnel)</FieldLabel>
              <input type="file" id="edit-vid-input" multiple accept="video/*" onChange={handleVideoUpload} className="hidden" />
              <label htmlFor="edit-vid-input"
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
                style={{ border: '0.5px dashed var(--c-border)', fontSize: 12, color: 'var(--c-text-muted)' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--c-text-muted)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--c-border)'}>
                <Video size={14} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
                {videos.length > 0 ? `${videos.length} vidéo(s) · Ajouter` : 'Ajouter une vidéo'}
              </label>
              {videos.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {videos.map(v => (
                    <div key={v.id} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                      <Video size={13} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 12, color: 'var(--c-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                      <button type="button" onClick={() => setVideos(p => p.filter(x => x.id !== v.id))} style={{ color: 'var(--c-error)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

        </div>

        {/* ══ COLONNE DROITE — Preview live ═══════════════════════════════ */}
        <div className="hidden xl:block" style={{ width: 280, flexShrink: 0 }}>
          <div className="sticky top-24 space-y-4">

            {/* Aperçu */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card overflow-hidden">
              <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '0.5px solid var(--c-border)', background: 'var(--c-primary-dark)' }}>
                <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.09em' }}>Aperçu catalogue</p>
                <div className="flex gap-1">
                  {['#FF5F57','#FFBD2E','#28CA41'].map(c => <div key={c} className="w-2 h-2 rounded-full" style={{ background: c, opacity: 0.7 }} />)}
                </div>
              </div>
              <div className="relative overflow-hidden" style={{ height: 130, background: 'var(--c-primary-dark)' }}>
                {images[mainImageIndex]?.preview
                  ? <img src={images[mainImageIndex].preview} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  : <div className="w-full h-full flex flex-col items-center justify-center gap-2"><ImageIcon size={26} style={{ color: 'var(--c-border)' }} /><p style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>Aucune image</p></div>}
                {productType === 'digital' && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(251,191,36,0.15)', border: '0.5px solid rgba(251,191,36,0.4)', backdropFilter: 'blur(4px)' }}>
                    <Zap size={9} style={{ color: '#F59E0B' }} /><span style={{ fontSize: 9, fontWeight: 600, color: '#F59E0B' }}>Digital</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                {category && (
                  <span className="inline-block px-2 py-0.5 rounded-full mb-2" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', color: 'rgb(var(--c-accent-green-rgb))', fontSize: 10, fontWeight: 600 }}>
                    {CATEGORIES.find(c => c.id === category)?.label || category}
                  </span>
                )}
                <p style={{ fontSize: 14, fontWeight: 600, color: productName ? 'var(--c-ink)' : 'var(--c-text-muted)', marginBottom: 4, lineHeight: 1.3 }}>
                  {productName || 'Nom du produit'}
                </p>
                {shortDesc && <p style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 8, lineHeight: 1.4 }}>{shortDesc.length > 70 ? shortDesc.slice(0, 70) + '…' : shortDesc}</p>}
                <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--c-ink)', fontVariantNumeric: 'tabular-nums' }}>
                  {previewPrice || <span style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>— Prix —</span>}
                </p>
                {stock && <p style={{ fontSize: 11, color: parseInt(stock) > 0 ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-error)', marginTop: 4 }}>{parseInt(stock) > 0 ? `${stock} en stock` : 'Rupture de stock'}</p>}
                {productType === 'digital' && (
                  <p style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 4 }}>
                    🎫 {existingCoupons.filter(c => !c.isUsed).length} coupon(s) disponible(s)
                  </p>
                )}
                <div className="mt-4 pt-3" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                  <div className="py-2 rounded-lg text-center" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.1)', fontSize: 11, fontWeight: 600, color: 'rgb(var(--c-accent-green-rgb))' }}>
                    Ajouter au panier
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Complétion */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.09em' }}>Complétion</p>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--c-accent-green-rgb))', fontVariantNumeric: 'tabular-nums' }}>{progress}%</span>
              </div>
              <div className="rounded-full overflow-hidden mb-4" style={{ height: 4, background: 'var(--c-primary-dark)' }}>
                <motion.div animate={{ width: `${progress}%` }} transition={{ duration: 0.45, ease: 'easeOut' }} className="h-full rounded-full" style={{ background: 'rgb(var(--c-accent-green-rgb))' }} />
              </div>
              <div className="space-y-2">
                {completionFields.map((field, i) => (
                  <motion.div key={field.label} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.04 }} className="flex items-center gap-2.5">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                      style={field.done ? { background: 'rgb(var(--c-accent-green-rgb))' } : { border: '1.5px solid var(--c-border)' }}>
                      {field.done && <Check size={9} style={{ color: '#000' }} />}
                    </div>
                    <span style={{ fontSize: 12, color: field.done ? 'var(--c-ink)' : 'var(--c-text-muted)', fontWeight: field.done ? 500 : 400 }}>{field.label}</span>
                    {field.done && <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: 'rgb(var(--c-accent-green-rgb))' }} />}
                  </motion.div>
                ))}
              </div>
              <motion.button whileHover={{ scale: saving ? 1 : 1.02 }} whileTap={{ scale: saving ? 1 : 0.98 }}
                onClick={handleSubmit} disabled={saving}
                className="w-full mt-4 py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-40"
                style={{ background: 'rgb(var(--c-accent-green-rgb))', color: '#000', border: '1px solid rgb(var(--c-accent-green-rgb))', fontSize: 13 }}>
                {saving ? <div className="animate-spin rounded-full h-4 w-4" style={{ border: '2px solid rgba(0,0,0,0.2)', borderTopColor: 'currentColor' }} /> : <Save size={14} />}
                {saving ? 'Mise à jour…' : 'Mettre à jour'}
              </motion.button>
            </motion.div>

          </div>
        </div>

      </div>

      {/* ── Bouton mobile sticky ───────────────────────────────────────────── */}
      <div className="xl:hidden fixed bottom-0 left-0 right-0 p-4" style={{ background: 'var(--c-primary-card)', borderTop: '0.5px solid var(--c-border)', zIndex: 40 }}>
        <motion.button whileTap={{ scale: 0.97 }} onClick={handleSubmit} disabled={saving}
          className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
          style={{ background: 'rgb(var(--c-accent-green-rgb))', color: '#000' }}>
          {saving ? <div className="animate-spin rounded-full h-4 w-4" style={{ border: '2px solid rgba(0,0,0,0.25)', borderTopColor: '#000' }} /> : <Save size={15} />}
          {saving ? 'Mise à jour…' : 'Mettre à jour le produit'}
        </motion.button>
      </div>
      <div className="xl:hidden h-20" />

      </div>
    </div>
  );
}
